import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// import auth + firestore bits to read trial dates
import { onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase/firebaseServices";
import { useAuth } from "../firebase/AuthContext";

import AiTrialPeriod from "../components/AiTrialPeriod/AiTrialPeriod";
import "../styles/components/homepymt.css";
import "../App.css";

// --- TrialDays (reads Firestore, mirrors your existing calc) ---
function TrialDays() {
  const { currentUser } = useAuth();
  const [trialStart, setTrialStart] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);
  const [trialActive, setTrialActive] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const reference = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(reference, (snapshot) => {
      const user = snapshot.data() || {};
      const start =
        user?.startTrialPeriodDate?.toDate?.() ||
        (user?.startTrialPeriodDate instanceof Date ? user.startTrialPeriodDate : null);
      const end =
        user?.endTrialPeriodDate?.toDate?.() ||
        (user?.endTrialPeriodDate instanceof Date ? user.endTrialPeriodDate : null);
      const isActive = user?.isTrialPeriodActive === true && end && end > new Date();
      setTrialStart(start || null);
      setTrialEnd(end || null);
      setTrialActive(Boolean(isActive));
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  const now = Date.now();
  const daysLeft =
    trialEnd != null
      ? Math.max(0, Math.ceil((trialEnd.getTime() - now) / (24 * 60 * 60 * 1000)))
      : 0;

  // Only show the pill if we know the trial window exists
  if (!(trialStart && trialEnd)) return null;

  return (
    <div className="phn-trial-days" aria-live="polite">
      {trialActive ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "0 days left"}
    </div>
  );
}

export default function Home() {
  const [openIdx, setOpenIdx] = useState(null);
  const navigate = useNavigate();

  const planImageUrl = `${process.env.PUBLIC_URL}/assets/images/AI_Nutrition.png`;
  const isCheckoutEnabled = process.env.REACT_APP_ENABLE_CHECKOUT === "true";
  const generateId = () => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
  };

  const simpleHash = (text) => {
    let hash = 5381;
    for (let index = 0; index < text.length; index += 1) {
      hash = ((hash << 5) + hash) ^ text.charCodeAt(index);
    }
    return (hash >>> 0).toString(16);
  };

  const buildEventMeta = (payload) => {
    const nowIso = new Date().toISOString();
    const requestId = generateId();
    const traceId = requestId;
    const changeHash = simpleHash(JSON.stringify(payload));
    return {
      source: "phn.ui.home",
      requestId,
      traceId,
      visited: [],
      hopCount: 0,
      publishedAt: nowIso,
      idempotencyKey: `home:${requestId}`,
      changeHash,
    };
  };

  const handleImageError = (event) => {
    event.currentTarget.style.backgroundImage =
      `url("data:image/svg+xml;utf8,` +
      encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='400'>
           <rect width='100%' height='100%' fill='#efefef'/>
           <text x='50%' y='50%' font-family='Arial' font-size='20' text-anchor='middle' fill='#888'>
             Image not found
           </text>
         </svg>`
      ) +
      `")`;
  };

  /**
   * billingAmount      = actual amount PayPal charges per billing period (matches PayPal plan)
   * displayPrice       = number shown on the card (per-month equivalent)
   * billingPeriodMonths= length of the billing period (1, 6, 12)
   */
  const plans = [
    {
      name: "Flex",
      billingAmount: 30.0, // PayPal plan bills $30 every month
      displayPrice: "30.00",
      currency: "USD",
      billingPeriodMonths: 1,
      billingSuffix: "/month",
      description:
        "Unlimited monthly access to AI nutrition tools, health calculators, and resources. Cancel anytime.",
      badge: null,
      features: [
        "Unlimited AI nutrition tools",
        "Health & wellness calculators",
        "Resource library access",
        "Billed monthly · cancel anytime",
      ],
      productId: "Flex_Plan",
      planId: process.env.REACT_APP_PAYPAL_PLAN_ID_MONTHLY,
      hostedButtonId: null,
    },
    {
      name: "Committed",
      billingAmount: 150.0, // PayPal plan bills $150 every 6 months
      displayPrice: "25.00", // effective per-month equivalent
      currency: "USD",
      billingPeriodMonths: 6,
      billingSuffix: "*6 months",
      description:
        "For people committed to 6 months of steady health and weight management.",
      badge: "Most Popular",
      features: [
        "Best for 6-month health and weight-loss focus",
        "6-month term · renews every 6 months",
        "One $150 charge per term (≈ $25/mo)",
        "Save $30 vs paying monthly",
        "Unlimited AI tools & calculators",
        "Priority feature access",
      ],
      productId: "Committed_Plan",
      planId: process.env.REACT_APP_PAYPAL_PLAN_ID_6M,
      hostedButtonId: null,
    },
    {
      name: "Dedicated",
      billingAmount: 240.0, // PayPal plan bills $240 every 12 months
      displayPrice: "20.00", // effective per-month equivalent
      currency: "USD",
      billingPeriodMonths: 12,
      billingSuffix: "*12 months",
      description:
        "For people who want a long-term healthy lifestyle and ongoing weight management.",
      badge: null,
      features: [
        "Ideal for passionate, year-round healthy living",
        "Renews annually ($240)",
        "≈ $20/mo (save $120 vs monthly)",
        "Unlimited AI tools & calculators",
        "Greatest long-term value",
        "Priority feature access",
      ],
      productId: "Dedicated_Plan",
      planId: process.env.REACT_APP_PAYPAL_PLAN_ID_12M,
      hostedButtonId: null,
    },
  ];

  const goToCheckout = (plan) => {
    if (!isCheckoutEnabled) {
      return;
    }

    const billingAmount =
      typeof plan.billingAmount === "number"
        ? plan.billingAmount
        : Number(plan.price || 0);

    const cleanPlan = {
      name: plan.name,
      price: billingAmount,
      displayPrice:
        plan.displayPrice ??
        (Number.isFinite(billingAmount)
          ? billingAmount.toFixed(2)
          : String(plan.price || "")),
      currency: plan.currency || "USD",
      billingSuffix: plan.billingSuffix || "/month",
      billingPeriodMonths: plan.billingPeriodMonths ?? null,
      description: plan.description || "",
      productId: plan.productId ?? null,
      hostedButtonId: plan.hostedButtonId ?? null,
      planId: plan.planId ?? null,
    };

    const envelope = {
      ...cleanPlan,
      __event: buildEventMeta(cleanPlan),
    };

    sessionStorage.setItem("phn_checkout_plan", JSON.stringify(envelope));
    navigate("/checkout", { state: { plan: envelope } });
  };

  return (
    <div className="home-container">
      <main className="home-main">
        <link rel="preload" as="image" href={planImageUrl} />

        <section
          className="hero"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.4)), url(${process.env.PUBLIC_URL}/assets/images/weightlossmessage.jpg)`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <h1>Unlock Smarter Eating with AI</h1>
          <p>
            Snap a photo of your meal and let our AI analyze its nutrition.
            Track your food habits, gain insights, and take control of your
            health journey today.
          </p>

          {/* Wrap the button + days in a scoped flex container */}
          <div className="phn-trial-cta">
            <AiTrialPeriod
              requireEmailVerified={false}
              onRequireLogin={() => navigate("/signin?intent=trial")}
              onAlreadyActive={() => navigate("/my-nutrition-status")}
              onSuccess={() => navigate("/my-nutrition-status")}
            />
            {/* Days left pill sits right under the button */}
            <TrialDays />
          </div>
        </section>

        <section className="pricing modal-skin">
          <h2>Launching Soon Choose Your Plan</h2>
          <div className="plans-grid">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`plan-card${plan.badge ? " popular" : ""}`}
              >
                <div
                  className="plan-media"
                  role="img"
                  aria-label="AI nutrition payments banner"
                  style={{ backgroundImage: `url('${planImageUrl}')` }}
                  onError={handleImageError}
                />
                <h3>
                  {plan.name}{" "}
                  {plan.badge && <span className="badge">{plan.badge}</span>}
                </h3>
                <p className="plan-price">
                  <span className="currency">$</span>
                  {/* Show the per-month style price on the card */}
                  {plan.displayPrice ??
                    (typeof plan.billingAmount === "number"
                      ? plan.billingAmount.toFixed(2)
                      : plan.price)}
                  <span className="per-month">
                    {plan.billingSuffix || "/month"}
                  </span>
                </p>
                <p className="plan-desc">{plan.description}</p>
                <ul>
                  {(Array.isArray(plan.features) ? plan.features : []).map(
                    (feature) => (
                      <li key={feature}>
                        <span className="feature-check">✔</span> {feature}
                      </li>
                    )
                  )}
                </ul>
                <div className="plan-card-btn-bottom">
                  <button
                    type="button"
                    className="button"
                    style={{
                      display: "block",
                      marginLeft: "auto",
                      marginRight: "auto",
                      width: "auto",
                    }}
                    disabled={!isCheckoutEnabled}
                    aria-disabled={!isCheckoutEnabled}
                    onClick={() => goToCheckout(plan)}
                  >
                    Launching Soon
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="faq-section">
          <h2 className="faq-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                className={`faq-item${openIdx === index ? " open" : ""
                  }`}
                key={index}
                onClick={() =>
                  setOpenIdx(openIdx === index ? null : index)
                }
              >
                <div className="faq-question">
                  {faq.question}
                  <span className="faq-arrow">
                    {openIdx === index ? "▲" : "▼"}
                  </span>
                </div>
                {openIdx === index && (
                  <div className="faq-answer">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

const faqs = [
  {
    question: "What is Pure Health Nexus?",
    answer:
      "Pure Health Nexus is an AI-powered nutrition membership that combines meal analysis with a My Nutrition Status dashboard and health calculators so you can see how your real eating pattern compares with science-based targets.",
  },
  {
    question: "How does the AI nutrition analysis work?",
    answer:
      "You can log meals using photos, voice or quick text. The AI breaks them into foods and portions and estimates energy, macros and key vitamins and minerals, then shows them in clear dashboards and tiles.",
  },
  {
    question: "What do I get with a membership?",
    answer:
      "You get access to AI nutrition tools, the My Nutrition Status dashboard, and a set of calculators and wellness tools, such as vitamin and mineral intake helpers, omega-3, BMI and body-fat, and strength calculators, plus educational content.",
  },
  {
    question: "Who is Pure Health Nexus for?",
    answer:
      "It is designed for adults who want to improve diet quality, support weight management and make more informed food and supplement choices without needing to become nutrition experts.",
  },
  {
    question: "Is this medical advice or a replacement for a doctor?",
    answer:
      "No. Pure Health Nexus is an educational tool that helps you understand patterns in your nutrition. It does not diagnose, treat, cure or prevent diseases and does not replace advice from doctors, dietitians or other health professionals.",
  },
  {
    question: "How does the 15-day free trial and billing work?",
    answer:
      "You can start with a 15-day free trial of the AI tools. After the trial, you can choose a monthly, 6-month or yearly subscription. Payments are handled securely through PayPal, and the current prices are always shown in the pricing section and at checkout.",
  },
  {
    question: "How is my personal data and privacy handled?",
    answer:
      "Pure Health Nexus is built with a GDPR-aware approach. Your nutrition data is used to provide the service, not sold, and data handling is explained in the Privacy Policy and Cookie Policy linked in the footer. AI use is limited and described transparently.",
  },
  {
    question: "Which devices can I use?",
    answer:
      "You can use Pure Health Nexus in a modern web browser on a computer, tablet or smartphone. When you sign in with the same account, your nutrition data and dashboards are available across your devices.",
  },
];
